import { PdfAnnotationIcon, PdfAnnotationSubtype } from "@paperviewpdf/models";

/**
 * Pin drops: stamp annotations that mark a spot on a page for a comment or a
 * reference link.
 *
 * Paperview PDF's XFDF import does not carry a stamp's image, so a pin loaded
 * through xfdfPlugin.importXfdf() has no appearance and is invisible. Pins are
 * therefore imported here instead: the stamp is read from the XFDF and handed
 * to the annotation plugin together with the pin image.
 *
 * The image is never read from the XFDF. Which pin image to use comes from the
 * kind of record the XFDF belongs to (conversation or reference).
 */

export var PIN_TYPES = {
  COMMENT: "comment",
  REFERENCE: "reference",
};

// Size of a pin on the page, in PDF points. Same as Papercurve v2.
export var PIN_SIZE = { width: 20, height: 28 };

// The pin artwork is 27x38. It is rasterized larger than that so the pin
// stays sharp when the page is zoomed in.
var PIN_IMAGE_WIDTH = 27;
var PIN_IMAGE_HEIGHT = 38;
var PIN_IMAGE_SCALE = 4;

var PIN_OUTLINE_PATH =
  "M13.0625,38 C12.290625,38 11.568625,37.6116875 11.126875,36.971625 L10.9713125,36.7495625 L1.598375,19.3241875 C0.5521875,17.4135 0,15.2486875 0,13.0625 C0,5.8603125 5.8603125,0 13.0625,0 C20.2646875,0 26.125,5.8603125 26.125,13.0625 C26.125,15.2486875 25.571625,17.415875 24.52425,19.32775 L15.1536875,36.7495625 L14.991,36.9799375 C14.54925,37.61525 13.8308125,38 13.0625,38 Z";

var PIN_COMMENT_ICON_PATH =
  "M8.49661266,22 C8.41319347,22 8.32927477,21.9790203 8.25334832,21.9365615 C8.07302301,21.8361587 7.97311979,21.6348537 8.00259124,21.4295526 L8.45914895,18.2351472 C6.34569636,17.1072399 5,14.8958821 5,12.501202 C5,8.91667447 7.91667447,6 11.5017015,6 L14.483313,6 C18.0678405,6 20.984515,8.91667447 20.984515,12.501202 C20.984515,16.0857294 18.0678405,19.0029034 14.4828135,19.0029034 L12.2739534,19.0029034 L8.81630296,21.8846118 C8.72489151,21.9610377 8.61100184,22 8.49661266,22 Z M11.5017015,6.99903219 C8.46714121,6.99903219 5.99903219,9.46714121 5.99903219,12.501202 C5.99903219,14.6326371 7.26080984,16.5927383 9.21341825,17.4958634 C9.4137242,17.5882739 9.52961194,17.8010677 9.49814242,18.0198558 L9.17495551,20.2851613 L11.7734382,18.1192595 C11.8633511,18.0448316 11.9762418,18.0038712 12.0931285,18.0038712 L14.4828135,18.0038712 C17.5173738,18.0038712 19.9854828,15.5357622 19.9854828,12.501202 C19.9854828,9.46664169 17.5173738,6.99903219 14.4828135,6.99903219 L11.5017015,6.99903219 Z";

var PIN_REFERENCE_ICON_PATH =
  "M13.5,16 L12,16 C11.724,16 11.5,15.7765 11.5,15.5 C11.5,15.2235 11.724,15 12,15 L13.5,15 C14.603,15 15.5,14.103 15.5,13 C15.5,11.897 14.603,11 13.5,11 L8,11 C6.897,11 6,11.897 6,13 C6,14.103 6.897,15 8,15 C8.276,15 8.5,15.2235 8.5,15.5 C8.5,15.7765 8.276,16 8,16 C6.3455,16 5,14.6545 5,13 C5,11.3455 6.3455,10 8,10 L13.5,10 C15.1545,10 16.5,11.3455 16.5,13 C16.5,14.6545 15.1545,16 13.5,16 Z M18,18.5 L12.5,18.5 C10.8455,18.5 9.5,17.1545 9.5,15.5 C9.5,13.8455 10.8455,12.5 12.5,12.5 L14,12.5 C14.2765,12.5 14.5,12.724 14.5,13 C14.5,13.276 14.2765,13.5 14,13.5 L12.5,13.5 C11.397,13.5 10.5,14.397 10.5,15.5 C10.5,16.603 11.397,17.5 12.5,17.5 L18,17.5 C19.103,17.5 20,16.603 20,15.5 C20,14.397 19.103,13.5 18,13.5 C17.7235,13.5 17.5,13.276 17.5,13 C17.5,12.724 17.7235,12.5 18,12.5 C19.6545,12.5 21,13.8455 21,15.5 C21,17.1545 19.6545,18.5 18,18.5 Z";

// Pin colour and icon per pin type. Same artwork as Papercurve v2.
var PIN_ARTWORK = {
  comment: { fill: "#FF9C79", iconPath: PIN_COMMENT_ICON_PATH },
  reference: { fill: "#7A6A8D", iconPath: PIN_REFERENCE_ICON_PATH },
};

// Annotation tool ids of the two pin tools.
export var PIN_TOOL_IDS = {
  comment: "papercurvePinComment",
  reference: "papercurvePinReference",
};

// Toolbar button, dropdown menu and commands of the pin tools.
var MAIN_TOOLBAR_ID = "main-toolbar";
var SELECT_TOOL_BUTTON_ID = "pointer-button";
var PIN_BUTTON_ID = "pc-pin-button";
var PIN_MENU_ID = "pc-pin-menu";
var PIN_BUTTON_COMMAND_ID = "papercurve:pin-menu";
var PIN_COMMANDS = {
  comment: {
    id: "papercurve:pin-comment",
    menuItemId: "pc-pin-comment",
    label: "Pin a Comment",
    icon: "comment",
  },
  reference: {
    id: "papercurve:pin-reference",
    menuItemId: "pc-pin-reference",
    label: "Pin a Reference",
    icon: "papercurveReference",
  },
};

// One pending or finished rasterization per pin type.
var pinImageDataPromises = {};

/**
 * Builds the SVG data URI for a pin.
 *
 * @param {string} type - one of PIN_TYPES
 * @returns {string} data URI of the pin artwork
 */
export function getPinImageSrc(type) {
  var artwork = PIN_ARTWORK[type] || PIN_ARTWORK.comment;
  var svg =
    '<svg xmlns="http://www.w3.org/2000/svg" width="' +
    PIN_IMAGE_WIDTH * PIN_IMAGE_SCALE +
    '" height="' +
    PIN_IMAGE_HEIGHT * PIN_IMAGE_SCALE +
    '" viewBox="0 0 ' +
    PIN_IMAGE_WIDTH +
    " " +
    PIN_IMAGE_HEIGHT +
    '">' +
    '<path d="' +
    PIN_OUTLINE_PATH +
    '" fill="' +
    artwork.fill +
    '"/>' +
    '<path d="' +
    artwork.iconPath +
    '" fill="#FFFFFF"/>' +
    "</svg>";

  return "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svg);
}

/**
 * Rasterizes the pin artwork into the pixel data the annotation plugin needs
 * to give a stamp its appearance. The result is cached per pin type.
 *
 * @param {string} type - one of PIN_TYPES
 * @returns {Promise<ImageData>} rejects when the image cannot be drawn
 */
export function loadPinImageData(type) {
  if (pinImageDataPromises[type]) {
    return pinImageDataPromises[type];
  }

  pinImageDataPromises[type] = new Promise(function (resolve, reject) {
    var image = new Image();

    image.onload = function () {
      var canvas = document.createElement("canvas");
      canvas.width = PIN_IMAGE_WIDTH * PIN_IMAGE_SCALE;
      canvas.height = PIN_IMAGE_HEIGHT * PIN_IMAGE_SCALE;

      var context = canvas.getContext("2d");
      if (!context) {
        reject(new Error("Canvas is not available for the pin image"));
        return;
      }

      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      resolve(context.getImageData(0, 0, canvas.width, canvas.height));
    };

    image.onerror = function () {
      reject(new Error("Pin image failed to load"));
    };

    image.src = getPinImageSrc(type);
  }).catch(function (error) {
    // Do not cache a failure, so the next pin can try again.
    delete pinImageDataPromises[type];
    throw error;
  });

  return pinImageDataPromises[type];
}

/**
 * Tells whether an annotation is a pin (a stamp annotation).
 *
 * @param {Object} annotation - annotation object from the annotation plugin
 * @returns {boolean}
 */
export function isPinAnnotation(annotation) {
  if (!annotation) return false;
  return annotation.type === PdfAnnotationSubtype.STAMP;
}

/**
 * Builds the annotation tool that drops a pin of the given type.
 *
 * A pin is placed with one click, centred on the click, at PIN_SIZE. It can
 * be moved but not resized. The tool switches itself off after one pin.
 * matchScore outranks Paperview PDF's built-in "stamp" tool (score 1) so
 * pins already on the page, including ones loaded from XFDF, are not
 * resizable either.
 *
 * @param {string} type - one of PIN_TYPES
 * @param {string} name - user-facing tool name
 * @returns {Object} annotation tool definition for the annotation plugin
 */
function createPinTool(type, name) {
  return {
    id: PIN_TOOL_IDS[type],
    name: name,
    matchScore: function (annotation) {
      return isPinAnnotation(annotation) ? 2 : 0;
    },
    interaction: {
      exclusive: false,
      cursor: "crosshair",
      isDraggable: true,
      isResizable: false,
      lockAspectRatio: true,
    },
    defaults: {
      type: PdfAnnotationSubtype.STAMP,
      subject: "Stamp",
      imageSrc: getPinImageSrc(type),
      imageSize: PIN_SIZE,
    },
    behavior: {
      deactivateToolAfterCreate: true,
      selectAfterCreate: false,
    },
  };
}

/**
 * Builds the pin tools to register with the annotation plugin through the
 * viewer's config.annotations.tools.
 *
 * @param {string[]} pinTypes - the PIN_TYPES values the viewer offers
 * @returns {Object[]} annotation tool definitions
 */
export function getPinTools(pinTypes) {
  return pinTypes.map(function (type) {
    return createPinTool(type, PIN_COMMANDS[type].label);
  });
}

/**
 * Maps an annotation tool id back to its pin type.
 *
 * @param {string} toolId - annotation tool id
 * @returns {string|null} one of PIN_TYPES, or null when it is not a pin tool
 */
export function getPinTypeForToolId(toolId) {
  if (toolId === PIN_TOOL_IDS.comment) return PIN_TYPES.COMMENT;
  if (toolId === PIN_TOOL_IDS.reference) return PIN_TYPES.REFERENCE;
  return null;
}

/**
 * Returns the pin type of the annotation tool that is currently active.
 *
 * @param {Object} annotationPlugin - annotation plugin capability
 * @returns {string|null} a PIN_TYPES value, or null when no pin tool is active
 */
export function getActivePinType(annotationPlugin) {
  if (!annotationPlugin) return null;

  var activeTool = annotationPlugin.getActiveTool();
  if (!activeTool) return null;

  return getPinTypeForToolId(activeTool.id);
}

/**
 * Switches a pin tool on, or off when it is already the active tool. The
 * next click on a page then drops a pin of that type.
 *
 * @param {Object} annotationPlugin - annotation plugin capability
 * @param {string} pinType - a PIN_TYPES value
 * @returns {boolean} true when the tool is now switched on
 */
export function togglePinTool(annotationPlugin, pinType) {
  if (!annotationPlugin) return false;

  if (getActivePinType(annotationPlugin) === pinType) {
    annotationPlugin.setActiveTool(null);
    return false;
  }

  annotationPlugin.setActiveTool(PIN_TOOL_IDS[pinType]);
  return true;
}

/**
 * Reads the active annotation tool id from the viewer's store state.
 * Command predicates receive the store state, not the plugins.
 *
 * @param {Object} context - command context ({ state, documentId })
 * @returns {string|null}
 */
function getActiveToolIdFromContext(context) {
  var plugins = context.state && context.state.plugins;
  var annotationState = plugins && plugins.annotation;
  var documentState =
    annotationState &&
    annotationState.documents &&
    annotationState.documents[context.documentId];

  return documentState ? documentState.activeToolId : null;
}

/**
 * Registers the commands behind the pin toolbar button.
 *
 * With several pin types the button opens a dropdown that lists one command
 * per type. With a single pin type there is nothing to choose, so the button
 * switches that pin tool on directly.
 *
 * @param {Object} options
 * @param {Object} options.commandsPlugin - commands plugin capability
 * @param {Object} options.uiPlugin - ui plugin capability
 * @param {string[]} options.pinTypes - the PIN_TYPES values the viewer offers
 * @param {Function} options.onTogglePinTool - called with a PIN_TYPES value
 *   when the user picks a pin tool
 */
export function registerPinCommands(options) {
  var commandsPlugin = options.commandsPlugin;
  var uiPlugin = options.uiPlugin;
  var pinTypes = options.pinTypes;
  var onTogglePinTool = options.onTogglePinTool;

  if (!commandsPlugin) return;

  var hasSinglePinType = pinTypes.length === 1;

  commandsPlugin.registerCommand({
    id: PIN_BUTTON_COMMAND_ID,
    label: hasSinglePinType ? PIN_COMMANDS[pinTypes[0]].label : "Pins",
    icon: "papercurvePin",
    action: function (context) {
      if (hasSinglePinType) {
        onTogglePinTool(pinTypes[0]);
        return;
      }

      if (!uiPlugin) return;
      uiPlugin
        .forDocument(context.documentId)
        .toggleMenu(PIN_MENU_ID, PIN_BUTTON_COMMAND_ID, PIN_BUTTON_ID);
    },
    active: function (context) {
      var activePinType = getPinTypeForToolId(
        getActiveToolIdFromContext(context)
      );
      return activePinType !== null;
    },
  });

  if (hasSinglePinType) return;

  pinTypes.forEach(function (type) {
    commandsPlugin.registerCommand({
      id: PIN_COMMANDS[type].id,
      label: PIN_COMMANDS[type].label,
      icon: PIN_COMMANDS[type].icon,
      action: function () {
        onTogglePinTool(type);
      },
      active: function (context) {
        return getActiveToolIdFromContext(context) === PIN_TOOL_IDS[type];
      },
    });
  });
}

/**
 * Adds the pin button to the main toolbar, right after the select (pointer)
 * tool, together with its dropdown menu; or removes it when pins may not be
 * dropped.
 *
 * mergeSchema replaces a toolbar's whole item list, so the current list is
 * read back, the pin button taken out, and re-inserted when enabled.
 *
 * @param {Object} options
 * @param {Object} options.uiPlugin - ui plugin capability
 * @param {string[]} options.pinTypes - the PIN_TYPES values the viewer offers
 * @param {boolean} options.isEnabled - whether pins may be dropped right now
 */
export function updatePinToolbar(options) {
  var uiPlugin = options.uiPlugin;
  var pinTypes = options.pinTypes;
  var isEnabled = options.isEnabled;

  if (!uiPlugin) return;

  var schema = uiPlugin.getSchema();
  var toolbar = schema && schema.toolbars && schema.toolbars[MAIN_TOOLBAR_ID];
  if (!toolbar) return;

  var pinButton = {
    type: "command-button",
    id: PIN_BUTTON_ID,
    commandId: PIN_BUTTON_COMMAND_ID,
    variant: "icon",
  };

  var withPinButton = function (items) {
    var result = [];

    items.forEach(function (item) {
      if (item.id === PIN_BUTTON_ID) return;

      if (item.type === "group" && item.items) {
        result.push(
          Object.assign({}, item, { items: withPinButton(item.items) })
        );
      } else {
        result.push(item);
      }

      if (item.id === SELECT_TOOL_BUTTON_ID && isEnabled) {
        result.push(pinButton);
      }
    });

    return result;
  };

  var toolbars = {};
  toolbars[MAIN_TOOLBAR_ID] = Object.assign({}, toolbar, {
    items: withPinButton(toolbar.items),
  });

  var partialSchema = { toolbars: toolbars };

  if (pinTypes.length > 1) {
    var menus = {};
    menus[PIN_MENU_ID] = {
      id: PIN_MENU_ID,
      items: pinTypes.map(function (type) {
        return {
          type: "command",
          id: PIN_COMMANDS[type].menuItemId,
          commandId: PIN_COMMANDS[type].id,
        };
      }),
    };
    partialSchema.menus = menus;
  }

  uiPlugin.mergeSchema(partialSchema);
}

/**
 * Parses an XFDF date such as "D:20260930151917-04'00'".
 *
 * @param {string|null} value - the XFDF date attribute
 * @returns {Date|undefined} undefined when the value is missing or malformed
 */
function parseXfdfDate(value) {
  if (!value) return undefined;

  var match = value.match(
    /^D:(\d{4})(\d{2})(\d{2})(\d{2})?(\d{2})?(\d{2})?(?:([+-])(\d{2})'?(\d{2})?'?|Z)?/
  );
  if (!match) return undefined;

  var utcMilliseconds = Date.UTC(
    parseInt(match[1], 10),
    parseInt(match[2], 10) - 1,
    parseInt(match[3], 10),
    parseInt(match[4] || "0", 10),
    parseInt(match[5] || "0", 10),
    parseInt(match[6] || "0", 10)
  );

  var hasOffset = match[7] ? true : false;
  if (hasOffset) {
    var offsetMinutes =
      parseInt(match[8], 10) * 60 + parseInt(match[9] || "0", 10);
    var offsetMilliseconds = offsetMinutes * 60 * 1000;
    if (match[7] === "+") {
      utcMilliseconds -= offsetMilliseconds;
    } else {
      utcMilliseconds += offsetMilliseconds;
    }
  }

  return new Date(utcMilliseconds);
}

/**
 * Finds the <stamp> element of a given annotation in an XFDF document.
 *
 * @param {Document} xfdfDocument - parsed XFDF
 * @param {string} annotationId - the annotation's XFDF name attribute
 * @returns {Element|null}
 */
function findStampElement(xfdfDocument, annotationId) {
  var stamps = xfdfDocument.getElementsByTagName("stamp");

  for (var i = 0; i < stamps.length; i++) {
    if (stamps[i].getAttribute("name") === annotationId) {
      return stamps[i];
    }
  }

  return null;
}

/**
 * Reads a pin out of an XFDF string.
 *
 * Only the stamp whose name matches annotationId is read; anything else in
 * the XFDF (PDFTron exports often carry unrelated <modify> entries) is
 * ignored. The rect is returned as stored, in PDF space (origin bottom-left).
 *
 * @param {string} xfdfString - XFDF of a conversation or a reference
 * @param {string} annotationId - the record's xfdf_highlight_id
 * @returns {{ id: string, pageIndex: number, rect: Object, author: (string|undefined),
 *   created: (Date|undefined), modified: (Date|undefined) }|null}
 *   null when the XFDF has no stamp for annotationId or the stamp is malformed
 */
export function parsePinXfdf(xfdfString, annotationId) {
  if (!xfdfString || !annotationId) return null;
  if (xfdfString.indexOf("<stamp") === -1) return null;

  var xfdfDocument = new DOMParser().parseFromString(
    xfdfString,
    "application/xml"
  );
  var stamp = findStampElement(xfdfDocument, annotationId);
  if (!stamp) return null;

  var rectParts = (stamp.getAttribute("rect") || "")
    .split(",")
    .map(function (part) {
      return parseFloat(part);
    });
  var hasValidRect =
    rectParts.length === 4 &&
    rectParts.every(function (part) {
      return !isNaN(part);
    });
  if (!hasValidRect) return null;

  var pageIndex = parseInt(stamp.getAttribute("page") || "0", 10);
  if (isNaN(pageIndex)) return null;

  return {
    id: annotationId,
    pageIndex: pageIndex,
    rect: {
      origin: {
        x: Math.min(rectParts[0], rectParts[2]),
        y: Math.min(rectParts[1], rectParts[3]),
      },
      size: {
        width: Math.abs(rectParts[2] - rectParts[0]),
        height: Math.abs(rectParts[3] - rectParts[1]),
      },
    },
    author: stamp.getAttribute("title") || undefined,
    created: parseXfdfDate(stamp.getAttribute("creationdate")),
    modified: parseXfdfDate(stamp.getAttribute("date")),
  };
}

/**
 * Tells whether a record's XFDF holds a pin for its annotation.
 *
 * @param {string} xfdfString - XFDF of a conversation or a reference
 * @param {string} annotationId - the record's xfdf_highlight_id
 * @returns {boolean}
 */
export function isPinXfdf(xfdfString, annotationId) {
  return parsePinXfdf(xfdfString, annotationId) !== null;
}

/**
 * Imports a pin from XFDF into the viewer, with its image.
 *
 * XFDF stores coordinates with the origin at the bottom-left of the page; the
 * annotation plugin expects the origin at the top-left, so the rect is flipped
 * using the page height.
 *
 * @param {Object} options
 * @param {Object} options.annotationPlugin - annotation plugin capability
 * @param {Object} options.documentManagerPlugin - document manager capability
 * @param {string} options.xfdfString - XFDF of a conversation or a reference
 * @param {string} options.annotationId - the record's xfdf_highlight_id
 * @param {string} options.type - one of PIN_TYPES; picks the pin image
 * @returns {Promise<boolean>} true when the pin was handed to the viewer
 */
export async function importPinXfdf(options) {
  var annotationPlugin = options.annotationPlugin;
  var documentManagerPlugin = options.documentManagerPlugin;

  if (!annotationPlugin || !documentManagerPlugin) return false;

  var pin = parsePinXfdf(options.xfdfString, options.annotationId);
  if (!pin) return false;

  var activeDocument = documentManagerPlugin.getActiveDocument();
  var page =
    activeDocument && activeDocument.pages
      ? activeDocument.pages[pin.pageIndex]
      : null;
  if (!page) {
    console.warn("[pin-drop] No page for pin", pin.id, "page", pin.pageIndex);
    return false;
  }

  var imageData;
  try {
    imageData = await loadPinImageData(options.type);
  } catch (error) {
    console.warn("[pin-drop] Pin image unavailable", error);
    return false;
  }

  var pageHeight = page.size.height;
  var annotation = {
    id: pin.id,
    type: PdfAnnotationSubtype.STAMP,
    pageIndex: pin.pageIndex,
    rect: {
      origin: {
        x: pin.rect.origin.x,
        y: pageHeight - (pin.rect.origin.y + pin.rect.size.height),
      },
      size: pin.rect.size,
    },
    icon: PdfAnnotationIcon.Draft,
    subject: "Stamp",
    flags: ["print"],
    author: pin.author,
    created: pin.created || new Date(),
    modified: pin.modified,
  };

  annotationPlugin.importAnnotations([
    { annotation: annotation, ctx: { imageData: imageData } },
  ]);

  return true;
}
